import { mockMenuCategories } from './mockMenu';
import type { HallOrderItem, HallPaymentStatus, HallTable } from '../services/hallApi';
import type { MenuCategory } from '../services/ordersApi';

export const HALL_CASE_TABLE = 'table20';
export const HALL_CASE_PAID = 'paid';
export const HALL_CASE_TIPPED = 'tipped';
export const HALL_CASE_TABLE_ID = 'case-mock-table-20';
export const HALL_CASE_ORDER_ID = 'case-mock-order';
export const HALL_CASE_TIP_PATH = `/tip/${HALL_CASE_ORDER_ID}?case=${HALL_CASE_TABLE}`;

const TARGET = 3000;

export function isHallCaseTable(search: URLSearchParams) {
  return search.get('case') === HALL_CASE_TABLE;
}

export function isHallCasePaid(search: URLSearchParams) {
  return search.get('case') === HALL_CASE_PAID;
}

export function isHallCaseTipped(search: URLSearchParams) {
  return search.get('case') === HALL_CASE_TIPPED;
}

export function isHallCaseMockOrder(orderId: string | undefined) {
  return orderId === HALL_CASE_ORDER_ID;
}

function pickItems(categories: MenuCategory[]): HallOrderItem[] {
  const pool = categories
    .flatMap((c) => c.items)
    .filter((i) => !i.isUnavailable && Number.isFinite(i.price) && i.price >= 180 && i.price <= 1600)
    .sort((a, b) => a.price - b.price);

  const source = pool.length ? pool : mockMenuCategories.flatMap((c) => c.items);
  const picked: HallOrderItem[] = [];
  let sum = 0;
  const seen = new Set<string>();

  const mid = Math.floor(source.length / 3);
  const order = [...source.slice(mid), ...source.slice(0, mid)];

  for (const item of order) {
    if (sum >= TARGET - 80) break;
    if (seen.has(item.id) || seen.has(item.name)) continue;
    const price = Math.round(item.price);
    if (sum + price > TARGET + 250 && picked.length >= 3) continue;
    seen.add(item.id);
    seen.add(item.name);
    picked.push({ name: item.name, amount: 1, price, sum: price });
    sum += price;
  }

  if (sum < 2400) {
    for (const item of source) {
      if (sum >= TARGET - 80) break;
      if (seen.has(item.id) || seen.has(item.name)) continue;
      const price = Math.round(item.price);
      seen.add(item.id);
      picked.push({ name: item.name, amount: 1, price, sum: price });
      sum += price;
    }
  }

  return picked.length ? picked : [{ name: 'Сет на компанию', amount: 1, price: TARGET, sum: TARGET }];
}

export function buildCaseHallTable(
  categories: MenuCategory[],
  opts?: { billed?: boolean },
): HallTable {
  const items = pickItems(categories.length ? categories : mockMenuCategories);
  const sum = items.reduce((s, i) => s + i.sum, 0);
  const billed = Boolean(opts?.billed);
  return {
    tableId: HALL_CASE_TABLE_ID,
    tableNumber: 20,
    sectionName: 'Зал',
    order: {
      id: HALL_CASE_ORDER_ID,
      status: billed ? 'Closed' : 'New',
      closed: billed,
      sum,
      paid: billed ? sum : 0,
      remainder: billed ? 0 : sum,
      guestsCount: 2,
      waiterName: 'Анна',
      items,
      canPayBill: !billed,
      canTip: true,
    },
  };
}

export function caseTipAmount(sum: number) {
  return Math.max(50, Math.round((sum * 0.1) / 10) * 10);
}

export function buildCasePaidPayment(
  table: HallTable,
  opts?: { kind?: 'bill' | 'tip'; amount?: number },
): HallPaymentStatus {
  const order = table.order;
  const items = order?.items ?? [];
  const kind = opts?.kind ?? 'bill';
  const amount =
    kind === 'tip' ? (opts?.amount ?? caseTipAmount(order?.sum ?? 0)) : (order?.sum ?? 0);
  return {
    id: kind === 'tip' ? 'case-mock-tip-payment' : 'case-mock-payment',
    kind,
    status: 'paid',
    amount,
    tableId: table.tableId,
    tableNumber: table.tableNumber,
    sectionName: table.sectionName,
    waiterName: order?.waiterName ?? null,
    iikoOrderId: HALL_CASE_ORDER_ID,
    items,
  };
}
